import React from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { appUrl } from '../lib/host';
import PlanIcon from './PlanIcon';
import { CALL_CHARGES_NOTE, formatRupees } from '../lib/plans';
import usePlans from '../lib/usePlans';
import './PlanCards.css';

/** The website's Contact page with the plan (and company) already filled in. */
export const contactUrl = (plan, company) => appUrl(`/contact?${new URLSearchParams({ interest: plan.name, ...(company && { company }) })}`);

/** "Let's talk" opens Contact in a new tab, so customers keep their place in the app. */
export const openContact = (plan, company) => window.open(contactUrl(plan, company), '_blank', 'noopener');

/** The price block: fixed (monthly and/or yearly) or "Let's talk". */
export function PlanPrice({ plan: p }) {
  if (p.priceType === 'contact') return <div className="tcx-plan-price quote"><b>Let's talk</b><small>Pricing built around your team</small></div>;
  if (p.cycle === 'yearly') return <div className="tcx-plan-price"><b>{formatRupees(p.yearlyPrice)}</b><small>per user / year</small></div>;
  return (
    <div className="tcx-plan-price">
      <b>{formatRupees(p.price)}</b><small>per user / month</small>
      {p.cycle === 'both' && p.yearlyPrice && (
        <em className="tcx-plan-yearly">or {formatRupees(p.yearlyPrice)} per user / year{p.annualDiscount ? ` (save ${p.annualDiscount}%)` : ''}</em>
      )}
    </div>
  );
}

/**
 * The plans in the website's pricing style, in the admin's order. Fixed-price plans are chosen directly (`onChoose`),
 * "Let's talk" plans open the Contact page (`onTalk`). `canChange` is false for members who aren't the owner. Names,
 * prices, badges, buttons and features follow the Super Admin's catalog (a hidden button isn't shown).
 */
export default function PlanCards({ currentTier, canChange = true, onChoose, onTalk }) {
  const { plans, loaded } = usePlans();
  // Wait for the catalog (or for it to fail) so customers never see the built-in names or prices flash first.
  if (!loaded) return <div className="tcx-plan-cards loading" aria-busy="true" />;
  return (
    <div className="tcx-plan-cards">
      <div className="tcx-plan-grid">
        {plans.map((p) => {
          const isCurrent = p.code === currentTier;
          const contact = p.priceType === 'contact';
          return (
            <article key={p.code} className={`tcx-plan ${p.badge ? 'popular' : ''} ${isCurrent ? 'current' : ''}`} aria-label={p.name}>
              {p.badge && <span className="tcx-plan-flag">{p.badge.toUpperCase()}</span>}
              <PlanIcon plan={p} />
              <h3>{p.name}</h3>
              {p.description && <p className="tcx-plan-desc">{p.description}</p>}
              <PlanPrice plan={p} />
              {(isCurrent || p.buttonEnabled) && (
                <button type="button" className="tcx-plan-btn" disabled={isCurrent || (!canChange && !contact)}
                  onClick={() => (contact ? onTalk?.(p) : onChoose(p))}>
                  {isCurrent ? 'Current plan' : contact ? "Let's talk" : p.buttonText}
                  {!isCurrent && <ArrowRight size={15} />}
                </button>
              )}
              <div className="tcx-plan-features">
                <b>Features include:</b>
                {p.includes && <b>{p.includes}</b>}
                <ul>{p.features.map((f) => <li key={f}><Check size={15} />{f}</li>)}</ul>
              </div>
            </article>
          );
        })}
      </div>
      <p className="tcx-plan-note">{CALL_CHARGES_NOTE}</p>
    </div>
  );
}
